/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, Component, Inject, OnInit} from '@angular/core';
import {Subject} from 'rxjs';
import {BsModalRef} from 'ngx-bootstrap/modal';

import template from './create-passkey-modal.component.html';
import {AdnTranslateService} from '../../common/filters/adn-translate.service';
import {AuthenticatedUserResource} from '../../../components/api/resources/resource/authenticated-user';
import {WebAuthnResource} from '../../../components/api/resources/resource/webauthn';
import {deferMarkForCheck} from '../../common/util/defer-mark-for-check';

@Component({
  standalone: false,
  selector: 'adn-create-passkey-modal',
  template
})
export class CreatePasskeyModalComponent implements OnInit {
  registerCredentials: any;
  passkeyCount = 0;

  readonly onCompleted = new Subject<void>();

  model: any = {};
  error: any = null;
  isEmail2fa = false;
  codeSent = false;
  sendingEmailCode = false;
  credentialSaved = false;
  savingCredentials = false;
  /** Stable [disabled] flag — avoids NG0100 when codeSent flips mid-refresh. */
  fieldsDisabled = false;

  private _user: any;

  /** Set via BsModalService initialState — sync flags before first CD. */
  set user(value: any) {
    this._user = value;
    this.isEmail2fa = value?.twoFactorAuthType !== 'APP' || value?.login2faRequired !== true;
    this.syncFieldsDisabled();
  }

  get user(): any {
    return this._user;
  }

  constructor(
    public bsModalRef: BsModalRef,
    private readonly adnTranslate: AdnTranslateService,
    @Inject(AuthenticatedUserResource) private readonly authenticatedUserResource: any,
    @Inject(WebAuthnResource) private readonly webAuthnResource: any,
    private readonly cdr: ChangeDetectorRef
  ) {
  }

  ngOnInit(): void {
    this.model = {
      description: 'Passkey #' + (this.passkeyCount + 1)
    };
    this.syncFieldsDisabled();
    if (this.isEmail2fa) {
      // Defer so the first modal CD pass does not race the send-code state flip.
      deferMarkForCheck(this.cdr, () => this.sendEmailCode());
    }
  }

  translateError(err: {text?: string; code?: string; parameters?: any}): string {
    if (!err) {
      return '';
    }
    return this.adnTranslate.transform(err.text, err.code, err.parameters);
  }

  sendEmailCode(): void {
    this.sendingEmailCode = true;
    this.codeSent = false;
    this.syncFieldsDisabled();
    this.authenticatedUserResource.sendEmail2faCode('create-passkey').then((result: any) => {
      deferMarkForCheck(this.cdr, () => {
        if (result === true) {
          this.codeSent = true;
          this.error = null;
        } else {
          this.error = result.data;
        }
        this.sendingEmailCode = false;
        this.syncFieldsDisabled();
      });
    }).catch((result: any) => {
      deferMarkForCheck(this.cdr, () => {
        this.error = result?.data || result;
        this.sendingEmailCode = false;
        this.syncFieldsDisabled();
      });
    });
  }

  registerPasskey(): void {
    this.savingCredentials = true;
    if (!this.model.twoFactorAuthCode || this.model.twoFactorAuthCode.length < 6) {
      if (this.isEmail2fa) {
        this.error = {text: this.model.twoFactorAuthCode && this.model.twoFactorAuthCode.length < 6
          ? 'Your verification code should be at least 6 characters long'
          : 'Your verification code is required'};
      } else {
        this.error = {text: this.model.twoFactorAuthCode && this.model.twoFactorAuthCode.length < 6
          ? 'Your 2FA code should be at least 6 characters long'
          : 'Your 2FA code is required'};
      }
      this.savingCredentials = false;
      return;
    }

    this.error = null;
    this.webAuthnResource.registerCredentials(
      this.model.twoFactorAuthCode,
      this.model.description,
      this.registerCredentials
    ).then((result: any) => {
      deferMarkForCheck(this.cdr, () => {
        if (result) {
          this.credentialSaved = true;
        }
        this.savingCredentials = false;
      });
    }).catch((err: any) => {
      deferMarkForCheck(this.cdr, () => {
        this.savingCredentials = false;
        if (err.data?.code === 'error.auth.invalid.two.factor.auth.token') {
          this.error = {text: this.isEmail2fa
            ? 'The Verification Code you provided is invalid. Please try again'
            : 'The 2FA code you provided is invalid. Please try again'};
          this.codeSent = false;
          this.syncFieldsDisabled();
        } else {
          this.error = err.data;
        }
      });
    });
  }

  close(): void {
    this.onCompleted.next();
    this.bsModalRef.hide();
  }

  dismiss(): void {
    this.bsModalRef.hide();
  }

  private syncFieldsDisabled(): void {
    this.fieldsDisabled = this.isEmail2fa && !this.codeSent;
  }
}
